import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import classNames from 'classnames/bind';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import ButtonYdt from 'yoda-core-components/lib/components/Button/Button';
import Icon from 'yoda-core-components/lib/components/Icon';
import CloseBanner from 'yoda-site-components/lib/assets/svg/closeIconNew.svg';
import { selectedProductSkus } from 'yoda-site-components/lib/selectors/ProductOptionsSelector';
import { selectDeviceType } from 'yoda-site-components/lib/selectors/ContextSelector';
import ErrorBoundary from 'yoda-site-components/lib/components/ErrorBoundary/ErrorBoundary';
import SlidePanel from 'yoda-site-components/lib/components/SlidePanel/SlidePanel';
import * as styles from './ProductDescription.css';
import ProductPromoContent from '../ProductPromo/ProductPromo';
import ProductDescriptionSlider from '../ProductDescriptionSlider/ProductDescriptionSlider';
import ProductConstants from '../../helpers/ProductConstants';

const cx = classNames.bind(styles);
class ProductDescription extends Component {
    static defaultProps = {
        dataAutomationId: '',
        selectedProductLot: [],
        deviceType: {},
        enableSlider: false,
        isBundle: false,
    };

    static propTypes = {
        dataAutomationId: PropTypes.string,
        productDetails: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
        selectedProductLot: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        deviceType: PropTypes.object,
        enableSlider: PropTypes.bool,
        isBundle: PropTypes.bool,
    };

    constructor() {
        super();
        this.containerRef = React.createRef();
        this.state = {
            isExpanded: false,
            isSlidePanelOpen: false,
            isSlidePanelOpenOnce: false,
            isProductDescriptionOverflow: false,
        };
    }

    componentDidMount() {
        this.setIsProductDescriptionOverflow();
    }

    componentDidUpdate(prevProps) {
        const prevLotId = get(prevProps, 'selectedProductLot[0].id', '');
        const currentLotId = get(this.props, 'selectedProductLot[0].id', '');
        if (prevLotId !== currentLotId) {
            this.setIsProductDescriptionOverflow();
        }
    }

    setIsProductDescriptionOverflow = () => {
        this.setState({
            isProductDescriptionOverflow: this.containerRef?.current?.clientHeight >= 364,
        });
    };

    /* istanbul ignore next */
    productDescriptionViewHandler = () => {
        const { enableSlider = false } = this.props;
        if (enableSlider) {
            this.setState({ isSlidePanelOpen: true });
            this.setState((prevState) => ({
                isSlidePanelOpenOnce: prevState.isSlidePanelOpenOnce
                    ? prevState.isSlidePanelOpenOnce
                    : true,
            }));
        } else {
            this.setState((prevState) => ({
                isExpanded: !prevState.isExpanded,
            }));
        }
    };

    /* istanbul ignore next */
    onClosePanel = () => {
        this.setState({ isSlidePanelOpen: false });
    };

    renderSliderTitle = () => {
        return (
            <div
                className={dt([
                    'border-1',
                    'border-gray-15',
                    'border-solid',
                    'h-12',
                    'px-4',
                    'py-2',
                    'shadow-elevation1',
                    'text-black',
                    'flex',
                    'justify-center',
                ])}
            >
                <span
                    className={dt([
                        'font-open-sans-bold',
                        'my-1',
                        'text-base',
                        'flex',
                        'justify-center',
                    ])}
                >
                    Product Description
                </span>
                <ButtonYdt
                    buttonType="Text"
                    className={dt(['absolute', 'top-2.5', 'right-3'])}
                    onClick={this.onClosePanel}
                >
                    <Icon
                        automationId="at-ydt-bopis-store-icon"
                        iconType="svg"
                        width="28px"
                        height="28px"
                        viewBox="0 0 28 28"
                    >
                        <CloseBanner />
                    </Icon>
                </ButtonYdt>
            </div>
        );
    };

    render() {
        const {
            productDetails,
            dataAutomationId,
            selectedProductLot,
            deviceType,
            isBundle,
        } = this.props;
        const {
            isSlidePanelOpen,
            isSlidePanelOpenOnce,
            isExpanded,
            isProductDescriptionOverflow,
        } = this.state;
        const isEnergyStarAvailable =
            selectedProductLot.length && selectedProductLot[0]
                ? selectedProductLot[0].energyStar
                : false;
        const gradientStyles = [
            'before:bg-gradient-to-b',
            'before:from-transparent',
            'before:to-white',
        ];
        const showMoreStyles = [
            'max-h-[364px]',
            'overflow-hidden',
            'before:z-[2]',
            'before:absolute',
            'before:w-full',
            'before:h-20',
            'before:bottom-0',
            'relative',
        ];
        const stylesToRender = !isExpanded ? [...showMoreStyles, ...gradientStyles] : [''];
        const productDescription =
            selectedProductLot.length &&
            selectedProductLot[0].description &&
            selectedProductLot[0].description.length
                ? selectedProductLot[0].description
                : null;
        return (
            <section
                className={cx('productDescription')}
                id="productDescriptionContainer"
                data-automation-id={dataAutomationId}
                aria-label="productDescription"
            >
                <div className={isProductDescriptionOverflow ? dt([...stylesToRender]) : null}>
                    {/* eslint-disable react/no-danger */}
                    {isEmpty(productDescription) && !isBundle ? (
                        <div className={dt(['font-normal', 'text-base', 'leading-6'])}>
                            {ProductConstants.DESCRIPTION_UNAVAILABLE}
                        </div>
                    ) : (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: productDescription,
                            }}
                            ref={this.containerRef}
                        />
                    )}
                    <ErrorBoundary>
                        <ProductPromoContent
                            promoContent={productDetails.promotionalImage}
                            brandInfo={productDetails.brand}
                            automationId="product-promo-block"
                            isEnergyStarAvailable={isEnergyStarAvailable}
                        />
                    </ErrorBoundary>
                </div>
                {isProductDescriptionOverflow && !isExpanded ? (
                    <div className={dt(['pt-4', 'flex', 'justify-center', 'items-center'])}>
                        <ButtonYdt
                            automationId="at-product-desc-cta"
                            className={dt([
                                'w-auto',
                                'text-sm',
                                'sm:!font-open-sans-regular',
                                'leading-5',
                                '!font-normal',
                            ])}
                            onClick={this.productDescriptionViewHandler}
                            id="product-desc-cta"
                            size="btnSm"
                            buttonType="Link"
                        >
                            Full Description
                        </ButtonYdt>
                    </div>
                ) : null}
                {isSlidePanelOpenOnce ? (
                    <SlidePanel
                        isOpenPanel={isSlidePanelOpen}
                        panelHeaderRenderer={this.renderSliderTitle}
                        deviceType={deviceType}
                        backButton={false}
                        onClosePanel={this.onClosePanel}
                    >
                        <ProductDescriptionSlider productDescription={productDescription} />
                    </SlidePanel>
                ) : null}
            </section>
        );
    }
}

const mapStateToProps = (state) => ({
    selectedProductLot: selectedProductSkus(false)(state),
    deviceType: selectDeviceType(state),
});

export default connect(mapStateToProps)(ProductDescription);
